// Freight Billing Audit — on-demand, reconstructed from read-only sources.
//
// Owner ask (2026-09-07): which scanned orders had no freight before the
// scan, got none from the scan, and then had freight keyed on the Sage
// invoice by hand — and tell "keyed at scan" from "auto-quoted at scan".
// Nothing is stored: every open recomputes from the Sage invoice mirror,
// the BOL scan-stamp mirror, our presets/pushes, freight-error events and
// (production only) the sales-site order record + invoice queue.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  freightAuditApi,
  type FreightAuditBucket,
  type FreightAuditResponse,
} from '../services/freightAudit';
import { downloadCsv } from '../utils/csv';
import {
  ATTENTION_BUCKETS,
  BUCKET_LABELS,
  BUCKET_ORDER,
  TRUCKLOAD_MODE_LABELS,
  type TruckloadMode,
  defaultRange,
  isoDay,
  money,
  rowsToCsv,
  sourceLabel,
  truckloadLabel,
  truckloadParam,
} from './freightAuditUtils';
import styles from './FreightAuditPage.module.css';

const GOOD_BUCKETS: ReadonlySet<FreightAuditBucket> = new Set([
  'preset_from_portal',
  'keyed_at_scan',
  'auto_quoted_at_scan',
]);

export function FreightAuditPage() {
  const initial = useMemo(() => defaultRange(), []);
  const [dateFrom, setDateFrom] = useState(initial.date_from);
  const [dateTo, setDateTo] = useState(initial.date_to);
  const [carrier, setCarrier] = useState('');
  const [bucket, setBucket] = useState<FreightAuditBucket | ''>('keyed_on_invoice');
  const [truckload, setTruckload] = useState<TruckloadMode>('all');
  const [data, setData] = useState<FreightAuditResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const truckloadFilter = truckloadParam(truckload);
    try {
      const resp = await freightAuditApi.report({
        date_from: dateFrom,
        date_to: dateTo,
        ...(carrier.trim() ? { carrier: carrier.trim() } : {}),
        ...(bucket ? { bucket } : {}),
        ...(truckloadFilter === undefined ? {} : { truckload: truckloadFilter }),
      });
      setData(resp);
    } catch {
      setError('Failed to load the freight audit.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, carrier, bucket, truckload]);

  useEffect(() => {
    void load();
  }, [load]);

  const exportCsv = () => {
    if (!data) return;
    downloadCsv(`freight_audit_${data.date_from}_${data.date_to}.csv`, rowsToCsv(data.rows));
  };

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <h1>Freight Billing Audit</h1>
        <p>
          Where the freight on each Sage invoice came from: preset in the portal, keyed at the BOL
          scan, auto-quoted at the scan, or keyed on the invoice by hand afterwards. Computed live
          from read-only sources; nothing is stored.
        </p>
      </div>

      <form
        className={styles.filters}
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <label>
          Invoiced from
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} aria-label="Invoiced from" />
        </label>
        <label>
          Invoiced to
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} aria-label="Invoiced to" />
        </label>
        <label>
          Carrier
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. ECHO" aria-label="Carrier" />
        </label>
        <label>
          Truckload
          <select value={truckload} onChange={(e) => setTruckload(e.target.value as TruckloadMode)} aria-label="Truckload">
            {(Object.keys(TRUCKLOAD_MODE_LABELS) as TruckloadMode[]).map((m) => (
              <option key={m} value={m}>{TRUCKLOAD_MODE_LABELS[m]}</option>
            ))}
          </select>
        </label>
        <label>
          Bucket
          <select value={bucket} onChange={(e) => setBucket(e.target.value as FreightAuditBucket | '')} aria-label="Bucket">
            <option value="">All</option>
            {BUCKET_ORDER.map((b) => (
              <option key={b} value={b}>{BUCKET_LABELS[b]}</option>
            ))}
          </select>
        </label>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={loading}>
          {loading ? 'Loading…' : 'Run'}
        </button>
        <span className={styles.spacer} />
        <button type="button" className={styles.btn} onClick={exportCsv} disabled={!data || data.rows.length === 0}>
          Export CSV
        </button>
      </form>

      {data && (
        <>
          <div className={styles.tiles} data-testid="bucket-tiles">
            {BUCKET_ORDER.map((b) => (
              <button
                key={b}
                type="button"
                className={[
                  styles.tile,
                  bucket === b ? styles.tileActive : '',
                  ATTENTION_BUCKETS.has(b) ? styles.tileAttention : '',
                ].join(' ')}
                onClick={() => setBucket(bucket === b ? '' : b)}
                aria-pressed={bucket === b}
              >
                <div className={styles.tileCount}>{data.counts[b] ?? 0}</div>
                <div className={styles.tileLabel}>{BUCKET_LABELS[b]}</div>
              </button>
            ))}
          </div>

          <div className={styles.sources} data-testid="sources">
            <span>{data.total} invoiced orders {data.date_from} → {data.date_to}</span>
            <span>{data.truckload_total} truckload · {data.total - data.truckload_total} non-truckload</span>
            <span>Invoice mirror synced: {data.sources.invoice_mirror_synced_at ? new Date(data.sources.invoice_mirror_synced_at).toLocaleString() : '—'}</span>
            <span className={data.sources.sales_site === 'ok' ? styles.sourceOk : styles.sourceWarn}>
              Sales-site order record: {sourceLabel(data.sources.sales_site)}
            </span>
            <span className={data.sources.invoice_queue === 'ok' ? styles.sourceOk : styles.sourceWarn}>
              BOL scan record: {sourceLabel(data.sources.invoice_queue)}
            </span>
          </div>
          <ul className={styles.caveats}>
            {data.caveats.map((c) => (
              <li key={c}>{c}</li>
            ))}
          </ul>

          <div className={styles.tableWrap}>
            <table className={styles.table} aria-label="Freight audit rows">
              <thead>
                <tr>
                  <th>Sales order</th>
                  <th>Invoiced</th>
                  <th>Customer</th>
                  <th>Carrier</th>
                  <th>Truckload</th>
                  <th>Scanned</th>
                  <th className={styles.num}>Invoice freight</th>
                  <th className={styles.num}>On order record</th>
                  <th className={styles.num}>Scan billed</th>
                  <th>Verdict</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr>
                    <td colSpan={10} className={styles.empty}>No orders in this bucket for the window.</td>
                  </tr>
                ) : (
                  data.rows.map((r) => (
                    <tr key={`${r.sales_order_no}-${r.invoice_no}`} data-testid="audit-row">
                      <td>
                        {r.order_id ? (
                          <a href={`/portal/orders?orderId=${r.order_id}`} target="_blank" rel="noreferrer">{r.sales_order_no}</a>
                        ) : (
                          r.sales_order_no
                        )}
                        {r.customer_po_no && <div className={styles.basis}>PO {r.customer_po_no}</div>}
                        {r.rebill_of && (
                          <div className={styles.basis} data-testid="rebill-of">rebill of {r.rebill_of}</div>
                        )}
                      </td>
                      <td>
                        {r.invoice_date}
                        {r.edited_after_invoice && <div className={styles.basis}>edited {isoDay(r.invoice_updated)}</div>}
                      </td>
                      <td>
                        {r.customer_no ?? '—'}
                        {r.customer_name && <div className={styles.basis}>{r.customer_name}</div>}
                      </td>
                      <td>{r.carrier ?? '—'}</td>
                      <td>
                        <span
                          className={r.is_truckload ? styles.truckload : styles.notTruckload}
                          data-testid="truckload-flag"
                          title={r.is_truckload ? 'Truckload / logistics carrier: no live rate, freight keyed by hand' : 'LTL, parcel or other live-rated carrier'}
                        >
                          {truckloadLabel(r.is_truckload)}
                        </span>
                      </td>
                      <td>{isoDay(r.scan_at)}</td>
                      <td className={styles.num}>{money(r.invoice_freight)}</td>
                      <td className={styles.num}>{money(r.sales_site?.actual_freight_cost)}</td>
                      <td className={styles.num}>{money(r.invoice_queue?.real_freight_amount)}</td>
                      <td>
                        <span
                          className={[
                            styles.bucket,
                            ATTENTION_BUCKETS.has(r.bucket) ? styles.bucketAttention : '',
                            GOOD_BUCKETS.has(r.bucket) ? styles.bucketGood : '',
                          ].join(' ')}
                        >
                          {BUCKET_LABELS[r.bucket]}
                        </span>
                        {r.confidence === 'approx' && <span className={styles.approx} title={r.basis}>approx</span>}
                        <div className={styles.basis}>{r.basis}</div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
}

export default FreightAuditPage;
